import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, getToken, API_BASE, photoUrl } from '../../services/apiClient';

// Utility Strikes — Schedule and General Information (Ryan, Nov 4 2026): "a page to document any
// utility strikes for our company — upload pictures, and a section on how to prevent it next time."
// One record per strike: when and where, the utility hit and who owns it, who was digging and with
// what, the 811 ticket and the marks, what happened and who was called, the money, the root cause,
// and the prevention — with photos. Everyone who reaches this page can read and log one; the office,
// PMs and field supervisors edit; the owner and Executives delete.

// APWA marking colours: red electric, yellow gas, blue water, green sewer, orange telecom, purple irrigation.
const UTIL = {
  gas:            ['🔥', 'Gas',            '#ca8a04'],
  electric:       ['⚡', 'Electric',       '#dc2626'],
  water:          ['💧', 'Water',          '#2563eb'],
  sewer:          ['🟢', 'Sewer',          '#16a34a'],
  storm:          ['🌧️', 'Storm drain',    '#0e7490'],
  telecom:        ['📞', 'Telecom',        '#ea580c'],
  fiber:          ['🧵', 'Fiber',          '#ea580c'],
  cable_tv:       ['📺', 'Cable TV',       '#ea580c'],
  irrigation:     ['🚿', 'Irrigation',     '#7c3aed'],
  traffic_signal: ['🚦', 'Traffic signal', '#4c1d95'],
  other:          ['❓', 'Other',          '#6b7280'],
};
const utilOf = t => UTIL[t] || UTIL.other;
const CAUSES = {
  no_ticket:          'No 811 ticket',
  expired_ticket:     'Ticket had expired',
  mismarked:          'Marks were wrong',
  unmarked:           'Utility was not marked',
  not_potholed:       'Not potholed / exposed first',
  outside_zone:       'Dug outside the marked area',
  mechanical_in_zone: 'Machine dug inside the tolerance zone',
  abandoned_line:     'Abandoned or unknown line',
  depth_assumed:      'Depth assumed, not checked',
  crew_error:         'Crew error',
  other:              'Other',
};
const METHODS = { excavator: 'Excavator', backhoe: 'Backhoe', trencher: 'Trencher', directional_drill: 'Directional drill', vertical_drill: 'Vertical drill', auger: 'Auger', hand_digging: 'Hand digging', vacuum: 'Vacuum excavation', other: 'Other' };
const STATUS = { open: ['Open', '#fef3c7', '#92400e'], review: ['Under review', '#dbeafe', '#1e40af'], closed: ['Closed', '#d1fae5', '#065f46'] };
const BILLED = { unknown: 'Not known yet', us: 'Titan (our cost)', owner: 'Utility owner', insurance: 'Insurance claim', other: 'Someone else' };

const session = (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null') || {}; } catch { return {}; } })();
const CAN_EDIT = ['admin', 'Admin', 'Executive', 'ProjectManager', 'OfficeStaff', 'Accounting', 'FieldSupervisor', 'ShopWorker'].includes(session.role);
const CAN_DELETE = ['admin', 'Admin', 'Executive'].includes(session.role);

const when = s => { if (!s) return '—'; const d = new Date(String(s).length <= 10 ? s + 'T12:00:00' : s); return isNaN(d) ? String(s) : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) + (String(s).length > 10 ? ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''); };
const localNow = () => { const d = new Date(); const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
const money = n => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const btn = (bg, fg, border) => ({ background: bg, color: fg, border: border || 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
const field = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, background: '#fff' };
const lbl = { fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 };
const pillS = (bg, fg) => ({ fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '3px 8px', textTransform: 'uppercase', background: bg, color: fg, whiteSpace: 'nowrap' });

const EMPTY = {
  occurred_at: '', project_id: null, location: '', utility_type: 'gas', utility_owner: '', line_description: '', depth_in: '',
  crew_lead_id: '', reported_by: '', equipment_id: '', equipment_text: '', excavation_method: '',
  ticket_number: '', ticket_valid: null, marks_present: null, marks_accurate: null, potholed: null, mechanical_in_zone: null,
  description: '', damage: '', service_out: null, customers_out: '', injuries: null, injury_details: '', evacuated: null, called_911: null, owner_notified_at: '', nv811_notified: null,
  repaired_by: '', repair_cost: '', billed_to: 'unknown', claim_number: '', dirt_reported: null,
  root_cause: '', contributing: '', prevention: '', corrective_actions: '', toolbox_talk_at: '', shared_with: '', status: 'open',
};

export default function UtilityStrikes({ onBack }) {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);         // null closed · 'new' · a strike id
  const [show, setShow] = useState('open');        // open | all | closed
  const [toast, setToast] = useState('');
  const load = useCallback(() => apiFetch('/utility-strikes').then(d => { setData(d); setErr(''); }).catch(e => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  // A bell points at one strike — open it straight away.
  useEffect(() => { const id = sp.get('strike'); if (id) setOpen(id); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!toast) return undefined; const t = setTimeout(() => setToast(''), 5000); return () => clearTimeout(t); }, [toast]);
  const close = () => { setOpen(null); if (sp.get('strike')) setSp({ pane: 'strikes' }, { replace: true }); };

  const s = data && data.summary;
  const rows = ((data && data.strikes) || []).filter(r => show === 'all' ? true : show === 'closed' ? r.status === 'closed' : r.status !== 'closed');
  return (
    <div>
      {toast && <div style={{ position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)', zIndex: 5000, background: '#166534', color: '#fff', fontSize: 14, fontWeight: 700, padding: '10px 18px', borderRadius: 999, boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}>✓ {toast}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <button onClick={onBack} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' }}>← General Information</button>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>⚡ Utility Strikes</h1>
        <span style={{ flex: 1 }} />
        <button onClick={() => setOpen('new')} style={btn('#dc2626', '#fff')}>+ Log a utility strike</button>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16 }}>Every strike on record — what was hit, why, what it cost, and what we do differently next time. Log one the same day, with the pictures.</div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {s && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          {[[`${s.year}`, s.this_year, s.this_year === 0 ? '#16a34a' : '#dc2626'], [`${Number(s.year) - 1}`, s.last_year, '#6b7280'], ['Still open', s.open, s.open ? '#b45309' : '#16a34a']].map(([l, n, c]) => (
            <div key={l} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 16px', minWidth: 110 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{l}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: c, lineHeight: 1.1 }}>{n}</div>
            </div>
          ))}
          {Object.keys(s.by_type || {}).length > 0 && (
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 16px', flex: '1 1 260px' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{s.year} by utility</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(s.by_type).sort((a, b) => b[1] - a[1]).map(([t, n]) => { const [i, l, c] = utilOf(t); return <span key={t} style={{ ...pillS(c + '1a', c), textTransform: 'none', fontSize: 12 }}>{i} {l} · {n}</span>; })}
              </div>
              {Object.keys(s.by_cause || {}).length > 0 && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Causes: {Object.entries(s.by_cause).sort((a, b) => b[1] - a[1]).map(([c, n]) => `${CAUSES[c] || c} (${n})`).join(' · ')}</div>}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden', marginBottom: 12 }}>
        {[['open', 'Open'], ['all', 'All'], ['closed', 'Closed']].map(([k, l]) => (
          <button key={k} onClick={() => setShow(k)} style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 700, border: 'none', cursor: 'pointer', background: show === k ? '#111827' : '#fff', color: show === k ? '#fff' : '#374151' }}>{l}</button>
        ))}
      </div>

      {!data && !err && <div style={{ color: '#9ca3af', padding: 30 }}>Loading…</div>}
      {data && rows.length === 0 && <div style={{ background: '#fff', border: '1px dashed var(--border)', borderRadius: 12, padding: 24, color: '#9ca3af', fontSize: 14 }}>{show === 'open' ? 'No open strikes.' : 'No strikes on record.'}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(r => {
          const [i, l, c] = utilOf(r.utility_type); const st = STATUS[r.status] || STATUS.open;
          return (
            <div key={r.id} role="button" tabIndex={0} onClick={() => setOpen(r.id)} onKeyDown={e => { if (e.key === 'Enter') setOpen(r.id); }}
              style={{ background: '#fff', border: '1px solid var(--border)', borderLeft: `5px solid ${c}`, borderRadius: 12, padding: '12px 16px', cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 26, lineHeight: 1 }}>{i}</div>
              <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{l}{r.utility_owner ? ` · ${r.utility_owner}` : ''}</span>
                  <span style={pillS(st[1], st[2])}>{st[0]}</span>
                  {r.injuries ? <span style={pillS('#fee2e2', '#991b1b')}>Injuries</span> : null}
                  {r.service_out ? <span style={pillS('#fef3c7', '#92400e')}>Service out</span> : null}
                  {r.photo_count > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>📷 {r.photo_count}</span>}
                </div>
                <div style={{ fontSize: 13.5, color: '#111827', marginTop: 3 }}>{when(r.occurred_at)}{r.project_number ? ` · ${r.project_number}${r.project_name && !String(r.project_name).startsWith(String(r.project_number)) ? ' ' + r.project_name : ''}` : ''}{r.location ? ` · ${r.location}` : ''}</div>
                {r.description && <div style={{ fontSize: 13, color: '#374151', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</div>}
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {r.root_cause ? `Cause: ${CAUSES[r.root_cause] || r.root_cause}` : 'Cause not set yet'}
                  {r.crew_lead_name ? ` · ${r.crew_lead_name}` : ''}{r.repair_cost != null ? ` · ${money(r.repair_cost)}` : ''}
                  {!r.prevention && <span style={{ color: '#b45309', fontWeight: 700 }}> · prevention not written yet</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {open && <StrikeWindow id={open === 'new' ? null : open} onClose={close} onSaved={t => { setToast(t); load(); }} onDeleted={() => { close(); setToast('Record deleted.'); load(); }} />}
    </div>
  );
}

// The form fields read the record through a context, so they are stable components — defined inside the
// window they would remount on every keystroke and drop the cursor.
const FormCtx = React.createContext({ form: {}, set: () => {}, ro: true });
function In({ k, type = 'text', placeholder, style }) { const { form, set, ro } = React.useContext(FormCtx); return <input type={type} value={form[k] ?? ''} placeholder={placeholder} disabled={ro} onChange={e => set(k, e.target.value)} style={{ ...field, ...style }} />; }
function Ta({ k, rows = 3, placeholder }) { const { form, set, ro } = React.useContext(FormCtx); return <textarea value={form[k] ?? ''} rows={rows} placeholder={placeholder} disabled={ro} onChange={e => set(k, e.target.value)} style={{ ...field, resize: 'vertical', lineHeight: 1.45 }} />; }
function Sel({ k, options, blank }) { const { form, set, ro } = React.useContext(FormCtx); return (
  <select value={form[k] ?? ''} disabled={ro} onChange={e => set(k, e.target.value)} style={field}>
    {blank !== undefined && <option value="">{blank}</option>}
    {Object.entries(options).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
); }
// Yes / No / not known — three states, because "we don't know yet" is a real answer on the day.
function YN({ k, label }) { const { form, set, ro } = React.useContext(FormCtx); return (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
    <span style={{ fontSize: 13, color: '#374151', flex: '1 1 180px' }}>{label}</span>
    <span style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
      {[[1, 'Yes', '#dc2626'], [0, 'No', '#16a34a'], [null, '?', '#6b7280']].map(([v, l, c]) => {
        const on = (form[k] === v) || (v === null && (form[k] === null || form[k] === '' || form[k] === undefined));
        return <button key={String(v)} type="button" disabled={ro} onClick={() => set(k, v)} style={{ padding: '4px 11px', fontSize: 12, fontWeight: 800, border: 'none', cursor: ro ? 'default' : 'pointer', background: on ? c : '#fff', color: on ? '#fff' : '#374151' }}>{l}</button>;
      })}
    </span>
  </div>
); }
function Sec({ title, hint, children }) { return (
  <div style={{ marginTop: 18 }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', borderBottom: '2px solid #e5e7eb', paddingBottom: 4, marginBottom: 10 }}>{title}{hint && <span style={{ fontWeight: 500, color: '#6b7280' }}> — {hint}</span>}</div>
    {children}
  </div>
); }
function Grid({ children, cols = 2 }) { return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 10 }}>{children}</div>; }
function F({ label, children, span }) { return <div style={span ? { gridColumn: '1 / -1' } : undefined}><div style={lbl}>{label}</div>{children}</div>; }

// ── One strike: read it, log it, change it ─────────────────────────────────────────────────────────
function StrikeWindow({ id, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({ ...EMPTY, occurred_at: localNow() });
  const [photos, setPhotos] = useState([]);
  const [queued, setQueued] = useState([]);        // files picked before the record exists
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [people, setPeople] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [job, setJob] = useState(null);            // { id, project_number, name }
  const [jobQ, setJobQ] = useState('');
  const [jobHits, setJobHits] = useState([]);
  const [meta, setMeta] = useState(null);
  const canEdit = !id || CAN_EDIT;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    apiFetch('/users').then(u => setPeople((Array.isArray(u) ? u : []).filter(x => (x.status || 'active') === 'active' && !x.is_agent).map(x => ({ id: x.id, name: `${x.first_name || ''} ${x.last_name || ''}`.replace(/\s+/g, ' ').trim(), role: x.role })).sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    apiFetch('/equipment').then(e => setEquipment((Array.isArray(e) ? e : []).map(x => ({ id: x.id, label: `${x.asset_number ? x.asset_number + ' · ' : ''}${x.name || ''}`.trim() })).sort((a, b) => a.label.localeCompare(b.label)))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiFetch(`/utility-strikes/${id}`).then(d => {
      const f = { ...EMPTY };
      for (const k of Object.keys(EMPTY)) f[k] = d[k] == null ? (typeof EMPTY[k] === 'string' ? '' : EMPTY[k]) : d[k];
      setForm(f); setPhotos(d.photos || []);
      setJob(d.project_id ? { id: d.project_id, project_number: d.project_number, name: d.project_name } : null);
      setMeta({ created_by_name: d.created_by_name, created_at: d.created_at, updated_at: d.updated_at, reported_by_name: d.reported_by_name });
      setLoading(false);
    }).catch(e => { setErr(e.message); setLoading(false); });
  }, [id]);
  // Job search — by number or name.
  const jobTimer = useRef(null);
  useEffect(() => {
    if (!jobQ.trim()) { setJobHits([]); return undefined; }
    clearTimeout(jobTimer.current);
    jobTimer.current = setTimeout(() => apiFetch(`/time/find-job?q=${encodeURIComponent(jobQ.trim())}`).then(r => setJobHits(Array.isArray(r) ? r : [])).catch(() => setJobHits([])), 250);
    return () => clearTimeout(jobTimer.current);
  }, [jobQ]);

  async function uploadQueued(strikeId, files) {
    if (!files.length) return [];
    const fd = new FormData(); files.forEach(f => fd.append('files', f));
    const r = await fetch(`${API_BASE}/utility-strikes/${strikeId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `Photo upload failed (${r.status})`);
    return Array.isArray(d) ? d : [];
  }
  async function save() {
    if (!form.utility_type) return setErr('Which utility was hit?');
    if (!form.occurred_at) return setErr('When did it happen?');
    setBusy('save'); setErr('');
    try {
      const body = { ...form, project_id: job ? job.id : null };
      for (const k of ['depth_in', 'customers_out', 'repair_cost']) if (body[k] === '') body[k] = null;
      const saved = await apiFetch(id ? `/utility-strikes/${id}` : '/utility-strikes', { method: id ? 'PATCH' : 'POST', body: JSON.stringify(body) });
      let note = id ? 'Saved.' : 'Logged — the office and the Executives have been told.';
      if (queued.length) {
        try { const added = await uploadQueued(saved.id, queued); setPhotos(p => [...p, ...added]); setQueued([]); note += ` ${added.length} photo${added.length === 1 ? '' : 's'} added.`; }
        catch (e) { note += ` Photos did not upload: ${e.message}`; }
      }
      onSaved(note);
      if (!id) onClose();
    } catch (e) { setErr(e.message); }
    setBusy('');
  }
  async function addPhotos(files) {
    const list = Array.from(files || []).filter(f => /^image\//.test(f.type));
    if (!list.length) return;
    if (!id) { setQueued(q => [...q, ...list]); return; }
    setBusy('photo'); setErr('');
    try { const added = await uploadQueued(id, list); setPhotos(p => [...p, ...added]); }
    catch (e) { setErr(e.message); }
    setBusy('');
  }
  async function removePhoto(p) {
    if (!window.confirm('Remove this picture?')) return;
    try { await apiFetch(`/utility-strikes/${id}/photos/${p.id}`, { method: 'DELETE' }); setPhotos(ps => ps.filter(x => x.id !== p.id)); }
    catch (e) { setErr(e.message); }
  }
  async function del() {
    if (!window.confirm('Delete this strike record and its pictures? This cannot be undone.')) return;
    setBusy('del');
    try { await apiFetch(`/utility-strikes/${id}`, { method: 'DELETE' }); onDeleted(); }
    catch (e) { setErr(e.message); setBusy(''); }
  }

  const ro = !canEdit;
  const st = STATUS[form.status] || STATUS.open;
  const util = utilOf(form.utility_type);

  return (
    <FormCtx.Provider value={{ form, set, ro }}>
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 820, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #eef0f2', display: 'flex', alignItems: 'center', gap: 10, borderTop: `5px solid ${util[2]}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>{util[0]} {id ? `${util[1]} strike` : 'Log a utility strike'}{job ? ` · ${job.project_number}` : ''}</div>
            {meta && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Logged by {meta.created_by_name || '—'} · {when(meta.created_at)}{meta.updated_at !== meta.created_at ? ` · changed ${when(meta.updated_at)}` : ''}</div>}
          </div>
          {id && (
            <span style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }} title="Open until the cause and prevention are written; under review while the office works the claim; closed when it is all done">
              {Object.entries(STATUS).map(([k, [l, bg, fg]]) => <button key={k} type="button" disabled={ro} onClick={() => set('status', k)} style={{ padding: '5px 10px', fontSize: 11.5, fontWeight: 800, border: 'none', cursor: ro ? 'default' : 'pointer', background: form.status === k ? fg : '#fff', color: form.status === k ? '#fff' : fg }}>{l}</button>)}
            </span>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '4px 20px 16px' }}>
          {loading ? <div style={{ color: '#9ca3af', padding: 30, textAlign: 'center' }}>Loading…</div> : (<>
            {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 12 }}>{err}</div>}
            {ro && <div style={{ background: '#f3f4f6', color: '#6b7280', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginTop: 12 }}>Read only — the office, PMs and field supervisors change a record.</div>}

            <Sec title="What was hit, and where">
              <Grid>
                <F label="Utility"><Sel k="utility_type" options={Object.fromEntries(Object.entries(UTIL).map(([k, [i, l]]) => [k, `${i} ${l}`]))} /></F>
                <F label="Who owns it"><In k="utility_owner" placeholder="NV Energy, Southwest Gas, TMWA, AT&T, Charter…" /></F>
                <F label="When"><In k="occurred_at" type="datetime-local" /></F>
                <F label="Job">
                  {job ? (
                    <div style={{ ...field, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>{job.project_number}</b> {job.name && !String(job.name).startsWith(String(job.project_number)) ? job.name : ''}</span>
                      {!ro && <button type="button" onClick={() => setJob(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 16 }}>×</button>}
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input value={jobQ} disabled={ro} onChange={e => setJobQ(e.target.value)} placeholder="Job number or name…" style={field} />
                      {jobHits.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                          {jobHits.map(h => <div key={h.id} onClick={() => { setJob(h); setJobQ(''); setJobHits([]); }} style={{ padding: '7px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}><b>{h.project_number}</b> {h.name}</div>)}
                        </div>
                      )}
                    </div>
                  )}
                </F>
                <F label="Where exactly" span><In k="location" placeholder="Address, station, or where on the job — 'NE corner of Prater & 15th, in the sidewalk'" /></F>
                <F label="The line"><In k="line_description" placeholder="2-inch steel gas main, 12 kV primary, 6-inch water…" /></F>
                <F label="Depth (inches)"><In k="depth_in" type="number" placeholder="How deep it was" /></F>
              </Grid>
            </Sec>

            <Sec title="Who was digging, and with what">
              <Grid>
                <F label="Crew lead on site">
                  <select value={form.crew_lead_id || ''} disabled={ro} onChange={e => set('crew_lead_id', e.target.value)} style={field}>
                    <option value="">—</option>
                    {people.map(p => <option key={p.id} value={p.id}>{p.name}{p.role === 'FieldSupervisor' ? ' 👷' : ''}</option>)}
                  </select>
                </F>
                <F label="Reported by">
                  <select value={form.reported_by || ''} disabled={ro} onChange={e => set('reported_by', e.target.value)} style={field}>
                    <option value="">Me</option>
                    {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </F>
                <F label="How we were digging"><Sel k="excavation_method" options={METHODS} blank="—" /></F>
                <F label="Machine">
                  <select value={form.equipment_id || ''} disabled={ro} onChange={e => set('equipment_id', e.target.value)} style={field}>
                    <option value="">— not one of ours / not listed</option>
                    {equipment.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                  </select>
                </F>
                {!form.equipment_id && <F label="Machine, if not listed" span><In k="equipment_text" placeholder="Rented mini-ex, sub's trencher…" /></F>}
              </Grid>
            </Sec>

            <Sec title="811 ticket and the marks" hint="this is what the claim turns on">
              <Grid>
                <F label="USA / 811 ticket number"><In k="ticket_number" placeholder="2026110400123" /></F>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                  <YN k="ticket_valid" label="Valid ticket for that spot and day?" />
                  <YN k="marks_present" label="Was the utility marked?" />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 8 }}>
                  <YN k="marks_accurate" label="Were the marks where the line really was?" />
                  <YN k="potholed" label="Was it potholed / exposed by hand first?" />
                  <YN k="mechanical_in_zone" label="Was a machine digging inside the tolerance zone (24 in. either side of the marks)?" />
                </div>
              </Grid>
            </Sec>

            <Sec title="What happened">
              <Grid>
                <F label="What happened" span><Ta k="description" rows={3} placeholder="In plain words — what the crew was doing, what they hit, how it was found." /></F>
                <F label="Damage" span><Ta k="damage" rows={2} placeholder="What broke — cut line, nicked jacket, crushed conduit, leak…" /></F>
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 8 }}>
                  <YN k="injuries" label="Anyone hurt?" />
                  <YN k="service_out" label="Service knocked out?" />
                  <YN k="evacuated" label="Area evacuated?" />
                  <YN k="called_911" label="911 called?" />
                  <YN k="nv811_notified" label="811 told about the damage?" />
                  <YN k="dirt_reported" label="Reported to CGA DIRT?" />
                </div>
                {form.injuries ? <F label="Injuries — who and how bad" span><Ta k="injury_details" rows={2} /></F> : null}
                {form.service_out ? <F label="Customers out (about)"><In k="customers_out" type="number" /></F> : null}
                <F label="Utility owner told at"><In k="owner_notified_at" type="datetime-local" /></F>
              </Grid>
            </Sec>

            <Sec title="Repair and the money">
              <Grid>
                <F label="Repaired by"><In k="repaired_by" placeholder="The owner's crew, us, a sub…" /></F>
                <F label="Cost (estimate until billed)"><In k="repair_cost" type="number" placeholder="$" /></F>
                <F label="Who pays"><Sel k="billed_to" options={BILLED} /></F>
                <F label="Claim / invoice number"><In k="claim_number" /></F>
              </Grid>
            </Sec>

            <Sec title="Why it happened">
              <Grid>
                <F label="Root cause"><Sel k="root_cause" options={CAUSES} blank="— pick one once it is known" /></F>
                <F label="What else played in"><In k="contributing" placeholder="Faded marks, rain, rush, new operator, old as-builts…" /></F>
              </Grid>
            </Sec>

            <Sec title="How we prevent it next time" hint="the part that matters most">
              <Grid>
                <F label="Prevention" span><Ta k="prevention" rows={4} placeholder="What the crew will do differently — pothole every crossing inside the zone, call for a re-mark after rain, hand dig the last foot, check the ticket dates at the morning huddle…" /></F>
                <F label="Corrective actions taken" span><Ta k="corrective_actions" rows={2} placeholder="Retraining, a new checklist item, a change to the pre-dig form, a talk with the sub…" /></F>
                <F label="Shared with the crews on (toolbox talk)"><In k="toolbox_talk_at" type="date" /></F>
                <F label="Shared with"><In k="shared_with" placeholder="All field crews, the drilling crew, the shop…" /></F>
              </Grid>
            </Sec>

            <Sec title="Pictures" hint="the hit, the marks, the ticket, the repair">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {photos.map(p => (
                  <div key={p.id} style={{ position: 'relative' }}>
                    <a href={photoUrl(p.filename)} target="_blank" rel="noreferrer"><img src={photoUrl(p.filename)} alt={p.original_name || ''} title={`${p.original_name || ''}${p.by_name ? ' · ' + p.by_name : ''}`} style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 10, border: '1px solid #e5e7eb', display: 'block' }} /></a>
                    {!ro && <button type="button" onClick={() => removePhoto(p)} title="Remove" style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 999, border: 'none', background: '#111827', color: '#fff', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>×</button>}
                  </div>
                ))}
                {queued.map((f, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={URL.createObjectURL(f)} alt={f.name} style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 10, border: '2px dashed #93c5fd', display: 'block', opacity: 0.85 }} />
                    <button type="button" onClick={() => setQueued(q => q.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 999, border: 'none', background: '#111827', color: '#fff', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>×</button>
                  </div>
                ))}
                {!ro && (
                  <label style={{ width: 110, height: 110, borderRadius: 10, border: '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 12.5, fontWeight: 700, cursor: busy === 'photo' ? 'default' : 'pointer', background: '#f9fafb' }}>
                    <span style={{ fontSize: 22 }}>📷</span>{busy === 'photo' ? 'Adding…' : '+ Add pictures'}
                    <input type="file" accept="image/*" multiple style={{ display: 'none' }} disabled={busy === 'photo'} onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
                  </label>
                )}
                {!photos.length && !queued.length && ro && <span style={{ fontSize: 13, color: '#9ca3af' }}>No pictures.</span>}
              </div>
              {!id && queued.length > 0 && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{queued.length} picture{queued.length === 1 ? '' : 's'} go up when you save.</div>}
            </Sec>
          </>)}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #eef0f2', display: 'flex', alignItems: 'center', gap: 8 }}>
          {id && CAN_DELETE && <button onClick={del} disabled={!!busy} style={btn('#fff', '#b91c1c', '1.5px solid #fca5a5')}>{busy === 'del' ? 'Deleting…' : 'Delete'}</button>}
          {id && <span style={pillS(st[1], st[2])}>{st[0]}</span>}
          <span style={{ flex: 1 }} />
          <button onClick={onClose} disabled={busy === 'save'} style={btn('#fff', '#6b7280', '1.5px solid #d1d5db')}>{ro ? 'Close' : 'Cancel'}</button>
          {!ro && <button onClick={save} disabled={!!busy || loading} style={btn('#dc2626', '#fff')}>{busy === 'save' ? 'Saving…' : id ? 'Save changes' : 'Log it'}</button>}
        </div>
      </div>
    </div>
    </FormCtx.Provider>
  );
}
